import {
  detachLayoutProps,
  WSButton,
  WSMarginProps
} from "@wingspanhq/fe-component-library";
import { CSVLink } from "react-csv";
import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";
import { CustomFieldResourceType } from "@wingspanhq/wingspan-api-sdk";
import { queriesCustomFields } from "@query";

type Props = WSMarginProps & {
  fullWidth?: boolean;
};

export function useDownloadPayableExampleTemplate() {
  const queryLineItemCustomFields =
    queriesCustomFields.listCustomFields.useQueryAll({
      filter: { resourceType: { eq: CustomFieldResourceType.LINE_ITEM } }
    });
  const lineItemCustomFields = queryLineItemCustomFields.flatData;
  const defaultCurrency = useDefaultCurrency();
  // Headers are the payablesImport sheet labels (flatfile-platform
  // `payablesImport/fields.ts`), in sheet order, so Flatfile maps every column
  // of the example without help. Custom field columns carry the same
  // "(custom field)" suffix the sheet uses.
  const downloadExamplePayables = [
    {
      uniqueReferenceKey: "INV-2026-001",
      payeeId: "",
      externalId: "",
      email: "wade@wingspan.app",
      engagementName: "Website redesign",
      amount: "100.50",
      quantity: "",
      unitCost: "",
      dueDate: "2026-12-01",
      paidDate: "",
      description: "Website updates & copy",
      detail: "Homepage and pricing page",
      reimbursable: "false",
      notes: "Website re-brand project",
      attachmentFileId: "",
      ...(lineItemCustomFields.reduce((acc: Record<string, string>, field) => {
        acc[field.name ?? ""] = "";
        return acc;
      }, {}) || {})
    }
  ];
  const csvProps = {
    filename: `Bulk_Upload_Payables_Template.csv`,
    headers: [
      { label: "Unique Reference Key", key: "uniqueReferenceKey" },
      { label: "Wingspan Payee ID", key: "payeeId" },
      { label: "External Payee ID", key: "externalId" },
      { label: "Email", key: "email" },
      { label: "Engagement Name", key: "engagementName" },
      { label: `Amount (${defaultCurrency})`, key: "amount" },
      { label: "Quantity", key: "quantity" },
      { label: `Unit Cost (${defaultCurrency})`, key: "unitCost" },
      { label: "Due date", key: "dueDate" },
      { label: "Paid date", key: "paidDate" },
      { label: "Description", key: "description" },
      { label: "Detail", key: "detail" },
      { label: "Reimbursable", key: "reimbursable" },
      { label: "Notes", key: "notes" },
      { label: "Attachment ID", key: "attachmentFileId" },
      ...(lineItemCustomFields.map(field => ({
        label: `${field.name ?? ""} (custom field)`,
        key: field.name ?? ""
      })) || [])
    ],
    data: downloadExamplePayables
  };

  return { csvProps, queryLineItemCustomFields };
}

export const DownloadPayableExampleTemplateButton: React.FC<Props> = ({
  fullWidth = true,
  ...otherProps
}) => {
  const { layoutProps } = detachLayoutProps(otherProps);
  const { csvProps, queryLineItemCustomFields } =
    useDownloadPayableExampleTemplate();
  return (
    <CSVLink {...csvProps}>
      <WSButton.Secondary
        disabled={queryLineItemCustomFields.isLoading}
        fullWidth={fullWidth}
        icon="download"
        {...layoutProps}
      >
        Download example
      </WSButton.Secondary>
    </CSVLink>
  );
};
